import urlcat from 'urlcat';

import { type Environment } from '~/constants/environments';

import { createKyInstance } from '@vestigas/data';

import { currentLocale } from '~/utils/i18n';
import { log } from '~/utils/logging';

import { Config } from '../Config';

import authService from './auth.service';

export { HTTPError, isAbortError } from '@vestigas/data';

/**
 * Get the API base URL based on the environment.
 *
 * @param {string} api - The API endpoint
 * @param {Environment | 'current'} env - The environment (e.g. 'staging')
 * @return {string} The base URL for the given environment and API
 */
const getBaseURL = (api: string, environment: Environment | 'current') => {
  if (environment === 'staging') {
    return urlcat(Config.stagingApiUrl, api);
  }

  if (environment === 'production') {
    return urlcat(Config.prodApiUrl, api);
  }

  return urlcat(Config.apiUrl, api);
};

/**
 * ky instance for working with the VESTIGAS API in the current environment.
 * @see https://app.dev.vestigas.com/
 */
export const vestigasApi = createKyInstance({
  authService,
  baseUrl: getBaseURL('', 'current'),
  extraHeaders: {
    'Admin-Portal-Version-Git': Config.gitHash ?? 'local-dev',
    'Admin-Portal-Version-Semantic': Config.version,
  },
  localeProvider: currentLocale,
  logger: log,
  withCaseConverter: true,
});

/**
 * ky instance for working with the VESTIGAS API in the STAGING environment.
 * @see https://app.dev.vestigas.com/
 */
export const vestigasApiStaging = createKyInstance({
  authService,
  baseUrl: getBaseURL('', 'staging'),
  extraHeaders: {
    'Admin-Portal-Version-Git': Config.gitHash ?? 'local-dev',
    'Admin-Portal-Version-Semantic': Config.version,
  },
  localeProvider: currentLocale,
  logger: log,
  withCaseConverter: true,
});

/**
 * ky instance for the VESTIGAS API in the current environment without automatic
 * case conversion.
 * Used when request/response contains user-defined keys that should not be transformed.
 */
export const vestigasApiNoConversion = createKyInstance({
  authService,
  baseUrl: getBaseURL('', 'current'),
  extraHeaders: {
    'Admin-Portal-Version-Git': Config.gitHash ?? 'local-dev',
    'Admin-Portal-Version-Semantic': Config.version,
  },
  localeProvider: currentLocale,
  logger: log,
  withCaseConverter: false,
});

/**
 * ky instance for the VESTIGAS API in the PRODUCTION environment without automatic
 * case conversion.
 * Used to copy environment-independent configuration (with user-defined keys) over to
 * production, e.g. data-sync task configs. Auth is shared across environments.
 */
export const vestigasApiProdNoConversion = createKyInstance({
  authService,
  baseUrl: getBaseURL('', 'production'),
  extraHeaders: {
    'Admin-Portal-Version-Git': Config.gitHash ?? 'local-dev',
    'Admin-Portal-Version-Semantic': Config.version,
  },
  localeProvider: currentLocale,
  logger: log,
  withCaseConverter: false,
});

/**
 * ky instance for working with the VESTIGAS AI API.
 * @see https://ai.vestigas.com/redoc
 */
export const vestigasAiApi = createKyInstance({
  authService,
  baseUrl: Config.aiApiUrl,
  extraHeaders: {
    'Admin-Portal-Version-Git': Config.gitHash ?? 'local-dev',
    'Admin-Portal-Version-Semantic': Config.version,
  },
  localeProvider: currentLocale,
  logger: log,
  withCaseConverter: true,
});
